import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import PDFView from './PDFView';
import './StudentFormsModal.css';

const StudentFormsModal = ({ student, onClose }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'view', 'edit', 'pdf'
  const [editFormData, setEditFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    if (student?.id) {
      fetchStudentForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  const fetchStudentForms = async () => {
    try {
      setLoading(true);
      console.log('Fetching forms for student intake:', student.id);
      const response = await axios.get(`/api/student-intake/district/${student.id}/forms`);
      console.log('Forms response:', response.data);
      // Debug: Check field counts for each form
      console.log('=== FORM FIELD COUNT DEBUG ===');
      response.data.forEach((form, index) => {
        const fieldCount = form.form_data ? Object.keys(form.form_data).length : 0;
        console.log(`Form ${index}: ${form.instance_name || form.master_form_name} - ${fieldCount} fields`);
        if (fieldCount === 21) {
          console.log('🚨 ALERT: This form has exactly 21 fields!');
          console.log('Form data keys:', Object.keys(form.form_data));
        }
        if (fieldCount < 21) {
          console.log('✅ Normal field count for:', form.instance_name || form.master_form_name);
        }
      });
      console.log('=== END FORM FIELD COUNT DEBUG ===');
      setForms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch student forms:', error);
      console.error('Error details:', error.response?.data);
      
      // Handle 404 (student not found) vs 500 (server error) differently
      if (error.response?.status === 404) {
        // Student not found - this shouldn't happen if we got here, but handle gracefully
        setForms([]);
      } else if (error.response?.status === 500) {
        // Server error - show error message but don't crash
        console.error('Server error fetching forms:', error.response?.data);
        setForms([]);
        // Don't show alert - we'll show a message in the UI instead
      } else {
        // Other errors
        setForms([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewForm = async (formId) => {
    try {
      const response = await axios.get(`/api/form-submissions/student-form/${formId}`);
      setSelectedForm(response.data);
      setViewMode('view');

      // Check if this is a PDF form (like School Lunch Application)
      const isPDFForm = response.data.master_form_name === 'School Lunch Application' ||
                       response.data.instance_name?.includes('PDF') ||
                       (response.data.form_data && response.data.form_data.form_type === 'family');

      if (isPDFForm) {
        // For PDF forms, don't try to render as a form - just show the data
        setEditFormData({
          formDefinition: null, // No form definition for PDF forms
          formValues: response.data.form_data
        });
        setViewMode('pdf'); // Use PDF view mode for PDF forms
      } else {
        // Parse form instance data for regular forms
        let instanceFormData = response.data.instance_form_data;
        if (typeof instanceFormData === 'string') {
          instanceFormData = JSON.parse(instanceFormData);
        }
        console.log('Instance form data sections:', instanceFormData?.sections?.length);
        instanceFormData?.sections?.forEach((section, index) => {
          console.log(`Section ${index}: ${section.fields?.length || 0} fields`);
        });
        const totalFields = instanceFormData?.sections?.reduce((sum, section) => sum + (section.fields?.length || 0), 0) || 0;
        console.log('Total fields in form definition:', totalFields);
        console.log('Form data keys:', Object.keys(response.data.form_data));

        setEditFormData({
          formDefinition: instanceFormData,
          formValues: response.data.form_data
        });
      }
    } catch (error) {
      console.error('Failed to fetch form:', error);
      alert('Failed to load form');
    }
  };

  const handleEditForm = () => {
    setViewMode('edit');
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      console.log('Saving form data:', editFormData.formValues);
      console.log('Field count:', Object.keys(editFormData.formValues).length);
      await axios.put(`/api/form-submissions/student-form/${selectedForm.id}`, {
        form_data: editFormData.formValues
      });
      alert('Form updated successfully');
      setViewMode('view');
      fetchStudentForms(); // Refresh list
    } catch (error) {
      console.error('Failed to update form:', error);
      alert('Failed to update form');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setViewMode('view');
    // Reset form data to original
    if (selectedForm) {
      setEditFormData({
        formDefinition: editFormData.formDefinition,
        formValues: selectedForm.form_data
      });
    }
  };

  const handleDeleteForm = async (formId) => {
    try {
      await axios.delete(`/api/form-submissions/student-form/${formId}`);
      alert('Form moved to trash');
      setShowDeleteConfirm(null);
      fetchStudentForms(); // Refresh list
      if (selectedForm?.id === formId) {
        setSelectedForm(null);
        setViewMode(null);
      }
    } catch (error) {
      console.error('Failed to delete form:', error);
      alert('Failed to delete form');
    }
  };

  const handleViewPDF = () => {
    setViewMode('pdf');
  };

  const handleFormChange = (newValues) => {
    setEditFormData({
      ...editFormData,
      formValues: newValues
    });
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal student-forms-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Student Forms - {student?.first_name} {student?.last_name}</h3>
            <button onClick={onClose} className="btn-close">&times;</button>
          </div>
          <div className="modal-body">
            <div>Loading forms...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal student-forms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Student Forms - {student?.first_name} {student?.last_name}</h3>
          <button onClick={onClose} className="btn-close">&times;</button>
        </div>
        <div className="modal-body">
          {!selectedForm ? (
            <>
              {forms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ fontSize: '1.1em', color: '#666', marginBottom: '0.5rem' }}>
                    No forms submitted for this student yet.
                  </p>
                  <p style={{ fontSize: '0.9em', color: '#999' }}>
                    Forms will appear here once the parent completes and submits enrollment forms.
                  </p>
                </div>
              ) : (
                <div className="forms-list">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Form Name</th>
                        <th>Submitted</th>
                        <th>Parent</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forms.map(form => (
                        <tr key={form.id}>
                          <td>{form.instance_name || form.master_form_name}</td>
                          <td>{form.submitted_at ? new Date(form.submitted_at).toLocaleDateString() : new Date(form.created_at).toLocaleDateString()}</td>
                          <td>{form.parent_first_name} {form.parent_last_name}</td>
                          <td className="actions">
                            <button 
                              onClick={() => handleViewForm(form.id)} 
                              className="btn-edit"
                            >
                              View
                            </button>
                            <button 
                              onClick={() => {
                                setShowDeleteConfirm(form.id);
                              }} 
                              className="btn-danger"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : viewMode === 'pdf' ? (
            <PDFView 
              form={selectedForm}
              formDefinition={editFormData?.formDefinition}
              formValues={selectedForm.form_data}
              onClose={() => setViewMode('view')}
            />
          ) : (
            <div className="form-view-container">
              <div className="form-view-header">
                <h4>{selectedForm.instance_name || selectedForm.master_form_name}</h4>
                <div className="form-view-actions">
                  {viewMode === 'edit' ? (
                    <>
                      <button 
                        onClick={handleSaveEdit} 
                        className="btn-primary"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button 
                        onClick={handleCancelEdit} 
                        className="btn-secondary"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={handleViewPDF} 
                        className="btn-primary"
                      >
                        View as PDF
                      </button>
                      <button 
                        onClick={handleEditForm} 
                        className="btn-secondary"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => {
                          setShowDeleteConfirm(selectedForm.id);
                        }} 
                        className="btn-danger"
                      >
                        Delete
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedForm(null);
                          setViewMode(null);
                        }} 
                        className="btn-secondary"
                      >
                        Back to List
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="form-view-content">
                {editFormData?.formDefinition ? (
                  <FormRenderer
                    formData={editFormData.formDefinition}
                    formValues={editFormData.formValues}
                    onChange={handleFormChange}
                    readOnly={viewMode !== 'edit'}
                  />
                ) : (
                  <div>Loading form...</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
            <div className="modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Delete</h3>
                <button onClick={() => setShowDeleteConfirm(null)} className="btn-close">&times;</button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this form submission?</p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  The form will be moved to trash and permanently deleted after 7 days.
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => handleDeleteForm(showDeleteConfirm)} 
                  className="btn-danger"
                >
                  Delete
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentFormsModal;

